import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Award,
  ExternalLink,
  Calendar,
  Filter,
  ArrowUpRight,
  X,
  Briefcase,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useData } from "@/contexts/DataContext";
import DomPurifyContent from "@/utils/domPurifyContent";
import CertificatePdfThumbnail from "@/components/ui/CertificatePdfThumbnail";
import {
  getCertificateFileUrl,
  isPdfFile,
} from "@/utils/certificateFile";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Certifications = () => {
  const { certifications, loading } = useData();
  const [selectedIndustry, setSelectedIndustry] = useState("All");
  const [selectedYear, setSelectedYear] = useState("All");

  const industries = useMemo(() => {
    const unique = [
      ...new Set(
        (certifications || []).map((c) => c.industry).filter(Boolean)
      ),
    ].sort();
    return ["All", ...unique];
  }, [certifications]);

  const years = useMemo(() => {
    const unique = [
      ...new Set(
        (certifications || [])
          .map((c) =>
            c.issueDate ? new Date(c.issueDate).getFullYear() : null
          )
          .filter(Boolean)
      ),
    ].sort((a, b) => b - a);
    return ["All", ...unique];
  }, [certifications]);

  const filteredCertifications = useMemo(() => {
    const list = (certifications || []).filter((cert) => {
      const matchesIndustry =
        selectedIndustry === "All" || cert.industry === selectedIndustry;
      const year = cert.issueDate
        ? new Date(cert.issueDate).getFullYear()
        : null;
      const matchesYear = selectedYear === "All" || year === selectedYear;
      return matchesIndustry && matchesYear;
    });

    return [...list].sort(
      (a, b) => new Date(b.issueDate) - new Date(a.issueDate)
    );
  }, [certifications, selectedIndustry, selectedYear]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.15 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.96 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: "spring", stiffness: 100, damping: 14 },
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="h-12 w-72 bg-muted animate-pulse rounded mx-auto mb-4" />
            <div className="h-5 w-96 max-w-full bg-muted animate-pulse rounded mx-auto" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-80 bg-muted animate-pulse rounded-lg"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">
            My{" "}
            <span className="bg-gradient-to-r from-purple-600 to-black bg-clip-text text-transparent">
              Certifications
            </span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Professional credentials and certifications that validate my
            expertise across industries and technologies.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="mb-12"
        >
          <div className="rounded-2xl border border-border/60 bg-gradient-to-br from-background via-background to-purple-50/40 dark:to-purple-950/20 shadow-sm p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-purple-600/10 text-purple-600">
                  <Filter className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold leading-none">
                    Refine results
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Filter by field or year issued
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-muted-foreground">
                  <span className="font-semibold text-foreground">
                    {filteredCertifications.length}
                  </span>{" "}
                  {filteredCertifications.length === 1
                    ? "certificate"
                    : "certificates"}
                </span>
                {(selectedIndustry !== "All" || selectedYear !== "All") && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-950/30"
                    onClick={() => {
                      setSelectedIndustry("All");
                      setSelectedYear("All");
                    }}
                  >
                    <X className="w-3.5 h-3.5 mr-1.5" />
                    Clear
                  </Button>
                )}
              </div>
            </div>

            <div className="grid gap-5 lg:grid-cols-[1fr_auto] lg:items-end">
              <div className="space-y-2.5 min-w-0">
                <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <Briefcase className="w-3.5 h-3.5" />
                  Industry / Field
                </div>
                <div
                  className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin"
                  role="listbox"
                  aria-label="Filter by industry"
                >
                  {industries.map((industry) => {
                    const active = selectedIndustry === industry;
                    return (
                      <button
                        key={industry}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => setSelectedIndustry(industry)}
                        className={[
                          "shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium transition-all border",
                          active
                            ? "bg-purple-600 text-white border-purple-600 shadow-sm shadow-purple-600/25"
                            : "bg-background/80 text-muted-foreground border-border hover:border-purple-300 hover:text-foreground",
                        ].join(" ")}
                      >
                        {industry === "All" ? "All fields" : industry}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2.5 w-full lg:w-[180px]">
                <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <Calendar className="w-3.5 h-3.5" />
                  Year issued
                </div>
                <Select
                  value={String(selectedYear)}
                  onValueChange={(value) =>
                    setSelectedYear(value === "All" ? "All" : Number(value))
                  }
                >
                  <SelectTrigger className="h-10 rounded-xl bg-background/80">
                    <SelectValue placeholder="All years" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={String(year)}>
                        {year === "All" ? "All years" : year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {(selectedIndustry !== "All" || selectedYear !== "All") && (
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border/60">
                <span className="text-xs text-muted-foreground mr-1">
                  Active:
                </span>
                {selectedIndustry !== "All" && (
                  <Badge
                    variant="secondary"
                    className="gap-1 pl-2.5 pr-1 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200"
                  >
                    {selectedIndustry}
                    <button
                      type="button"
                      aria-label={`Remove ${selectedIndustry} filter`}
                      className="ml-0.5 rounded-full p-0.5 hover:bg-purple-200/80 dark:hover:bg-purple-800/60"
                      onClick={() => setSelectedIndustry("All")}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {selectedYear !== "All" && (
                  <Badge
                    variant="secondary"
                    className="gap-1 pl-2.5 pr-1 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200"
                  >
                    {selectedYear}
                    <button
                      type="button"
                      aria-label={`Remove ${selectedYear} filter`}
                      className="ml-0.5 rounded-full p-0.5 hover:bg-purple-200/80 dark:hover:bg-purple-800/60"
                      onClick={() => setSelectedYear("All")}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence mode="popLayout">
            {filteredCertifications.map((cert) => (
              <motion.div
                key={cert._id}
                variants={itemVariants}
                layout
                exit={{ opacity: 0, scale: 0.9 }}
              >
                <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col group">
                  <Link
                    to={`/certifications/${cert._id}`}
                    className="relative aspect-[4/3] bg-muted overflow-hidden block"
                  >
                    {isPdfFile(cert.file) ? (
                      <CertificatePdfThumbnail file={cert.file} />
                    ) : (
                      <img
                        src={getCertificateFileUrl(cert.file)}
                        alt={cert.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )}
                  </Link>

                  <CardContent className="p-6 flex flex-col flex-1 gap-4">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant="secondary"
                          className="bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
                        >
                          {cert.industry}
                        </Badge>
                        {cert.company && (
                          <Badge variant="outline" className="text-xs">
                            {cert.company}
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {cert.issueDate
                            ? new Date(cert.issueDate).toLocaleDateString(
                                undefined,
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                }
                              )
                            : "—"}
                        </span>
                      </div>
                      <Link to={`/certifications/${cert._id}`}>
                        <h3 className="text-xl font-semibold leading-snug flex items-start gap-2 group-hover:text-purple-600 transition-colors">
                          <Award className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
                          <span>{cert.title}</span>
                        </h3>
                      </Link>
                    </div>

                    {cert.description && (
                      <DomPurifyContent
                        className="text-sm text-muted-foreground line-clamp-3 prose prose-sm dark:prose-invert max-w-none"
                        content={cert.description}
                      />
                    )}

                    <div className="mt-auto pt-2 flex flex-col gap-2">
                      <Button variant="outline" className="w-full" asChild>
                        <Link to={`/certifications/${cert._id}`}>
                          View Details
                          <ArrowUpRight className="w-4 h-4 ml-2" />
                        </Link>
                      </Button>
                      {cert.verifyUrl?.trim() ? (
                        <Button
                          className="w-full bg-purple-600 hover:bg-purple-700"
                          asChild
                        >
                          <a
                            href={cert.verifyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Verify
                            <ExternalLink className="w-4 h-4 ml-2" />
                          </a>
                        </Button>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {filteredCertifications.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20 text-muted-foreground"
          >
            <Award className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p>No certifications match the selected filters.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Certifications;
